import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import emailValidator from 'utils/validators/emailValidator'
import * as Yup from 'yup'
import advertiserInfoValidationRulesExtended from 'modules/Forms/Account/Create/subcomponents/advertiser-info-validation-rules-extended'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'
import AdvertiserInfoGroup from 'modules/Forms/Account/Create/subcomponents/AdvertiserInfoGroup'

export const DirectAdvertSchema = () =>
  Yup.object()
    .shape({
      logins: Yup.array().of(Yup.object()),
      login: Yup.string().when('logins', ([logins], schema) =>
        logins
          ? schema.strip()
          : schema
              .test('baseLoginTest', '', baseLoginValidator)
              .required('common::validation_messages.required'),
      ),
      network: Yup.string().required('common::validation_messages.required'),
      email: Yup.string()
        .test(
          'emailValidator',
          'common::validation_messages.incorrect_email_format',
          emailValidator,
        )
        .required('common::validation_messages.required'),
      password: Yup.string()
        .min(
          8,
          'services::forms.register_account.directadvert.fields.password.validation_messages.min_length',
        )
        .required('common::validation_messages.required'),
    })
    .concat(advertiserInfoValidationRulesExtended())

let CreateDirectAdvertForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkType,
  formHeader,
  form,
  networkId,
  change,
  array,
  multiLogins,
  initialValues,
}) => {
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit(form, onClose, multiLogins)
  const selector = formValueSelector(form)

  const {
    formValues: { advertiser_info },
  } = useSelector((state) => ({
    formValues: selector(state, 'email', 'advertiser_info'),
  }))

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)
    }
  }, [networkId])

  return (
    <>
      {formHeader}

      <form
        onSubmit={handleSubmit(submitAccountData)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={'margin-bottom-16'}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.directadvert.fields.login.label'}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="email"
            type="email"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.directadvert.fields.email.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            minLength={8}
            name="password"
            type="password"
            fullWidth
            normalize={(value = '') => value.replace(/ /g, '')}
            component={renderInputField}
            label={t('services::forms.register_account.directadvert.fields.password.label')}
            t={t}
            helperText={t(
              'services::forms.register_account.directadvert.fields.password.helperText',
            )}
          />
        </div>

        <AdvertiserInfoGroup
          form={form}
          change={change}
          advertiser_info={advertiser_info}
          networkType={networkType}
          initialValues={initialValues}
        />

        <SubmitButtons
          submitted={submitAccountData.submitted}
          submitting={submitting}
          onClose={onClose}
        />
      </form>
    </>
  )
}

CreateDirectAdvertForm = reduxForm({
  form: 'CreateDirectAdvertForm',
  initialValues: {
    login: '',
    network: '',
    email: '',
    password: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateDirectAdvertForm)

CreateDirectAdvertForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  change: PropTypes.func,
  networkType: PropTypes.string,
  networkId: PropTypes.string,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  initialValues: PropTypes.object,
  formHeader: PropTypes.node,
}

export default CreateDirectAdvertForm
